import { useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Crown, Ghost } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

interface NPCSelectorProps {
  characters: Character[]
  members: MemberType[]
  selectedCharacter: Character | null
  onSelectCharacter: (character: Character | null) => void
  isMaster: boolean
}

// Função para identificar o tipo do personagem (mesma lógica de CharacterList)
function getCharacterType(character: Character, membersByUserId: Map<string, MemberType>): 'mestre' | 'jogador' | 'npc' {
  // Se o personagem tem is_npc=true, é NPC
  if (character.isNpc === true) return 'npc'
  
  const member = membersByUserId.get(character.userId)
  if (!member) return 'npc' // Fallback: sem membro = NPC
  return member.role === 'mestre' ? 'mestre' : 'jogador'
}

export function NPCSelector({
  characters,
  members,
  selectedCharacter,
  onSelectCharacter,
  isMaster
}: NPCSelectorProps) {
  // Criar mapa de membros por userId para identificar tipo do personagem
  const membersByUserId = useMemo(() => {
    const map = new Map<string, MemberType>()
    members.forEach(member => {
      map.set(member.userId, member)
    })
    return map
  }, [members])

  // Filtrar apenas NPCs
  const npcs = useMemo(() => {
    return characters.filter(character => 
      getCharacterType(character, membersByUserId) === 'npc'
    )
  }, [characters, membersByUserId])

  // Se não for mestre ou não houver NPCs, não renderizar
  if (!isMaster || npcs.length === 0) {
    return null
  }

  const handleValueChange = (value: string) => {
    if (value === 'master') {
      onSelectCharacter(null)
    } else {
      const character = npcs.find(npc => npc.id === value)
      if (character) {
        onSelectCharacter(character)
      }
    }
  }

  const currentValue = selectedCharacter?.id || 'master'

  return (
    <div className="mb-2">
      <Select value={currentValue} onValueChange={handleValueChange}>
        <SelectTrigger className={cn(
          "w-full h-9 bg-white dark:bg-zinc-800/80 border-slate-200 dark:border-zinc-700/50 text-game-primary dark:text-zinc-100 hover:bg-game-hover dark:hover:bg-zinc-700/80",
          "focus:ring-violet-500/50 focus:ring-2 [&>span[data-slot='select-value']]:hidden"
        )}>
          <SelectValue placeholder="Selecione um NPC" />
          <div className="flex items-center gap-2 w-full">
            {selectedCharacter ? (
              <>
                <Avatar className="h-5 w-5">
                  <AvatarImage src={selectedCharacter.imageUrl} alt={selectedCharacter.name} />
                  <AvatarFallback className="bg-game-tertiary text-game-primary dark:bg-zinc-700 dark:text-zinc-100 text-xs">
                    {selectedCharacter.name[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <Ghost className="h-3.5 w-3.5 text-game-muted dark:text-zinc-400 shrink-0" />
                  <span className="text-sm truncate text-game-primary dark:text-zinc-100">{selectedCharacter.name}</span>
                </div>
              </>
            ) : (
              <>
                <Crown className="h-4 w-4 text-violet-500 dark:text-violet-400 shrink-0" />
                <span className="text-sm text-game-primary dark:text-zinc-100">Falando como Mestre</span>
              </>
            )}
          </div>
        </SelectTrigger>
        <SelectContent className="bg-white dark:bg-zinc-900/95 backdrop-blur-xl border-slate-200 dark:border-zinc-700/50">
          <SelectItem value="master" className="text-game-primary dark:text-zinc-100 focus:bg-game-hover dark:focus:bg-zinc-800 focus:text-game-primary dark:focus:text-zinc-100">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-violet-500 dark:text-violet-400" />
              <span className="text-game-primary dark:text-zinc-100">Mestre</span>
            </div>
          </SelectItem>
          {npcs.length > 0 && (
            <>
              <div className="h-px bg-slate-200 dark:bg-zinc-700/50 my-1" />
              {npcs.map((npc) => (
                <SelectItem
                  key={npc.id}
                  value={npc.id}
                  className="text-game-primary dark:text-zinc-100 focus:bg-game-hover dark:focus:bg-zinc-800 focus:text-game-primary dark:focus:text-zinc-100"
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={npc.imageUrl} alt={npc.name} />
                      <AvatarFallback className="bg-game-tertiary text-game-primary dark:bg-zinc-700 dark:text-zinc-100 text-xs">
                        {npc.name[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex items-center gap-1.5">
                      <Ghost className="h-3.5 w-3.5 text-game-muted dark:text-zinc-400" />
                      <span className="text-game-primary dark:text-zinc-100">{npc.name}</span>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  )
}
